import { Observable, Operator, OperatorFunction, Subscriber, Subscription } from 'rxjs';

interface Watchers {
  [token: string]: Subscription;
}

/**
 * Watches an array of string identifiers (usually tokens) and generates/destroys observables
 * for each one as subsequent arrays add/remove the identifiers.
 *
 * Initially will fire N times, one for each item in the token array, and then only once for each
 * token added, or if one of the inner observables fires.
 *
 * @param project a function to return an observable relevant for the given identifier
 */
export function watchMap<R>(project: (token: string) => Observable<R>): OperatorFunction<Array<string>, R> {
  return (source: Observable<Array<string>>) => source.lift(new WatchMapOperator(project));
}

class WatchMapOperator<T, R> implements Operator<T, R> {
  constructor(private project: (key: string, index: number) => Observable<R>) {}

  call(subscriber: Subscriber<R>, source: any): any {
    return source.subscribe(new WatchMapSubscriber(subscriber, this.project));
  }
}

/**
 * We need this JSDoc comment for affecting ESDoc.
 * @ignore
 * @extends {Ignored}
 */
class WatchMapSubscriber<R> extends Subscriber<Array<string>> {
  private watchers: Watchers;

  constructor(
    destination: Subscriber<R>,
    private project: (key: string, index: number) => Observable<R>
  ) {
    super(destination);
    this.watchers = {};
  }

  protected _next(value: Array<string>) {
    try {
      value
        .filter((key) => key != null)
        .forEach((key, index) => {
          if (this.watchers[key] == null) {
            // console.log('Watching:', key); //TODO: Delete when confident this thing works
            this.watchers[key] = this.project(key, index).subscribe((result) => this.destination.next(result));
          }
        });

      for (const key in this.watchers) {
        if (value.indexOf(key) < 0) {
          this.watchers[key].unsubscribe();
          delete this.watchers[key];
          // console.log('Unwatched:', key); //TODO: Delete when confident this thing works
        }
      }
    } catch (error) {
      this.destination.error(error);
      return;
    }
  }

  protected _unsubscribe() {
    for (const key in this.watchers) {
      if (this.watchers.hasOwnProperty(key)) {
        this.watchers[key].unsubscribe();
        delete this.watchers[key];
      }
    }
  }
}
